import { useServerAction } from "@/components/hooks/use-server-action";
import { logger } from "@/utils/logger";
import { useMemo } from "react";
import {
  checkUnreadInAppNotificationsForEmployer,
  fetchLatestInAppNotificationsForEmployer,
  getLastCheckedForEmployer,
  updateLastCheckedForEmployer,
} from "./in-app-notifications";

const refreshIntervalMs = 300000; // 5分間隔でポーリング

export function useInAppNotifications({ officeId }: { officeId: string }) {
  // 未読状態の管理
  // セッション切れ・事業所から外れた場合は Server Action が unauthorized() で例外になるので、
  // 共通のエラーハンドリング（ログイン画面への誘導など）がある useServerAction を使う
  const { data: unreadData, mutate: mutateUnread } = useServerAction(
    `inAppNotifications-unread-${officeId}`,
    () => checkUnreadInAppNotificationsForEmployer({ officeId }),
    {
      swrOptions: {
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
        refreshInterval: refreshIntervalMs,
      },
    },
  );

  // 最新通知の管理
  const { data: notificationsData, isLoading } = useServerAction(
    `inAppNotifications-latest-${officeId}`,
    () => fetchLatestInAppNotificationsForEmployer({ officeId }),
    {
      swrOptions: {
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
        refreshInterval: refreshIntervalMs,
      },
    },
  );

  // 最終確認時刻の管理
  const { data: lastCheckedData, mutate: mutateLastChecked } = useServerAction(
    `inAppNotifications-lastChecked-${officeId}`,
    () => getLastCheckedForEmployer({ officeId }),
    {
      swrOptions: {
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
        refreshInterval: refreshIntervalMs,
      },
    },
  );

  const notifications = useMemo(
    () => notificationsData ?? [],
    [notificationsData],
  );

  const lastCheckedAt = lastCheckedData?.lastCheckedAt ?? null;

  const hasUnread = unreadData?.hasUnread ?? false;

  const markAsReadAndUpdateLastChecked = () => {
    // キャッシュを既読に更新
    mutateUnread({ hasUnread: false }, { revalidate: false });

    // 最終確認時刻を更新（キャッシュは更新しない）
    updateLastCheckedForEmployer({ officeId }).catch((error) => {
      logger.error({ error, officeId }, "Failed to update last checked");
    });
  };

  const refreshLastChecked = () => {
    // 最終確認時刻のキャッシュを再取得
    mutateLastChecked();
  };

  return {
    hasUnread,
    notifications,
    isLoading,
    lastCheckedAt,
    markAsReadAndUpdateLastChecked,
    refreshLastChecked,
  };
}
